import * as React from "react";
import { cn } from "../lib/utils";

const THEMES = [
  { id: "bleu", label: "Bleu", color: "#3b82f6" },
  { id: "ambre", label: "Ambre", color: "#bb7125" },
  { id: "indigo", label: "Indigo", color: "#1c4286" },
  { id: "verdure", label: "Verdure", color: "#00b49b" },
  { id: "corail", label: "Corail", color: "#f48067" },
  { id: "crepuscule", label: "Crépuscule", color: "#40456a" },
  { id: "blanc", label: "Blanc", color: "#ffffff" },
  { id: "noir", label: "Noir", color: "#171717" },
] as const;

export type ThemeId = (typeof THEMES)[number]["id"];

/** Palettes de couleur Cliently (vitrine, paramètres app). */
export const CLIENTLY_COLOR_THEMES = THEMES;

const getStoredTheme = (): ThemeId => {
  if (typeof window === "undefined") return "bleu";
  return (localStorage.getItem("cliently-theme") as ThemeId) || "bleu";
};

const applyTheme = (theme: ThemeId) => {
  const root = document.documentElement;
  if (theme === "bleu") {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", theme);
  }
  localStorage.setItem("cliently-theme", theme);
};

/** Même logique que l’inline script dans index.html (évite un flash avant le bundle JS). */
export const initColorThemeFromStorage = () => {
  if (typeof window === "undefined") return;
  try {
    applyTheme(getStoredTheme());
  } catch {
    /* localStorage indisponible (mode privé strict, etc.) */
  }
};

export const useTheme = () => {
  const [theme, setThemeState] = React.useState<ThemeId>(getStoredTheme);

  React.useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  React.useEffect(() => {
    const stored = getStoredTheme();
    applyTheme(stored);
    setThemeState(stored);
  }, []);

  const setTheme = React.useCallback((t: ThemeId) => {
    setThemeState(t);
    applyTheme(t);
  }, []);

  return { theme, setTheme, themes: THEMES };
};

export const ThemeSwitcher = ({
  className,
  variant = "default",
}: {
  className?: string;
  /** `prominent` : pastilles plus grandes (ex. page Paramètres). */
  variant?: "default" | "prominent";
}) => {
  const { theme, setTheme } = useTheme();
  const prominent = variant === "prominent";

  return (
    <div
      className={cn(
        prominent
          ? "flex flex-wrap items-center gap-3 sm:gap-4"
          : "flex items-center gap-1.5",
        className,
      )}
    >
      {THEMES.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => setTheme(t.id)}
          title={t.label}
          aria-label={t.label}
          aria-pressed={theme === t.id}
          className={cn(
            "rounded-full border-2 transition-all cursor-pointer shrink-0",
            prominent
              ? "h-11 w-11 sm:h-12 sm:w-12 hover:scale-105 active:scale-95 shadow-sm"
              : "h-5 w-5 hover:scale-110",
            theme === t.id
              ? prominent
                ? "border-foreground ring-2 ring-foreground/25 ring-offset-2 ring-offset-background scale-105 shadow-md"
                : "border-foreground ring-foreground/20 scale-110 ring-2"
              : prominent
                ? "border-border/60 opacity-90 hover:border-border hover:opacity-100"
                : "border-transparent opacity-70 hover:opacity-100",
          )}
          style={{ backgroundColor: t.color }}
        />
      ))}
    </div>
  );
};
